import { Icons } from "@/components/ui/icons";
import { useThemeChoice } from "@/hooks/use-theme-choice";
import { useT } from "@/i18n";
import { panelsHeaderMessages } from "@/i18n/messages/panels-header";
import { setThemeChoice, type ThemeChoice } from "@/lib/theme";

export interface ThemeOption {
  id: ThemeChoice;
  label: string;
  icon: typeof Icons.Moon;
}

const THEME_OPTIONS = [
  { id: "dark", labelKey: "themeDark", icon: Icons.Moon },
  { id: "light", labelKey: "themeLight", icon: Icons.Sun },
] as const;

export interface ThemeCycle {
  theme: ThemeChoice;
  options: readonly ThemeOption[];
  active: ThemeOption;
  select: (next: ThemeChoice) => void;
  /** Dark → light → dark. */
  cycle: () => void;
}

/**
 * The app theme and its controls, shared by the rail's button and menu and
 * the wall's theme button.
 */
export function useThemeCycle(): ThemeCycle {
  const t = useT(panelsHeaderMessages);
  const theme = useThemeChoice();
  const options: readonly ThemeOption[] = THEME_OPTIONS.map((option) => ({
    id: option.id,
    label: t(option.labelKey),
    icon: option.icon,
  }));

  const select = (next: ThemeChoice) => {
    if (next !== theme) setThemeChoice(next);
  };
  const cycle = () => {
    const ids = options.map((option) => option.id);
    select(ids[(ids.indexOf(theme) + 1) % ids.length]!);
  };
  const active = options.find((option) => option.id === theme) ?? options[0]!;
  return { theme, options, active, select, cycle };
}
